import { createSlice } from "@reduxjs/toolkit";
import { showLoading, hideLoading } from "react-redux-loading-bar";
import { saveAs } from "file-saver";
import moment from "moment";

import { centToDollar } from "../../../app/utils";
import {
  getReferralClaims,
  updatePayoutStatus,
  voidReferralClaim,
} from "./referralClaimAPI";

export const referralClaimCsvCell = (value) => {
  if (value === null || value === undefined) return "";
  const text = String(value);
  const safe = /^[\t\r=+\-@]/.test(text) ? `'${text}` : text;
  return /[",\r\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

// nonPayable already means cancelled, so only an unpaid payable claim is worth voiding
const VOIDABLE_STATUSES = ["payable"];

export const referralClaimSlice = createSlice({
  name: "referralClaim",
  initialState: {
    claims: [],
    status: "idle",
    pagination: {},
    page: 0,
    rowsPerPage: 100,
    sort: "desc",
    sortBy: "createdAt",
    selectedIds: [],
    selectedClaims: [],
    selectedPayoutStatus: "payable",
    selectedCooledOff: true,
    selectedReferrerUserId: "",
    filterModalOpen: false,
    payoutModalOpen: false,
    voidModalOpen: false,
    successMessage: "",
    errorMessage: "",
  },
  reducers: {
    setSelectedIds: (state, action) => {
      state.selectedIds = action.payload;
    },
    setSelectedClaims: (state, action) => {
      state.selectedClaims = action.payload;
    },
    setSelectedPayoutStatus: (state, action) => {
      state.selectedPayoutStatus = action.payload;
    },
    setSelectedCooledOff: (state, action) => {
      state.selectedCooledOff = action.payload;
    },
    setSelectedReferrerUserId: (state, action) => {
      state.selectedReferrerUserId = action.payload;
    },
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setRowsPerPage: (state, action) => {
      state.rowsPerPage = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = action.payload;
    },
    setClaims: (state, action) => {
      state.claims = action.payload;
      state.status = "success";
    },
    showFilterModal: (state, action) => {
      state.filterModalOpen = action.payload;
    },
    showPayoutModal: (state, action) => {
      state.payoutModalOpen = action.payload;
    },
    showVoidModal: (state, action) => {
      state.voidModalOpen = action.payload;
    },
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload;
    },
  },
});

export const {
  setSelectedIds,
  setSelectedClaims,
  setSelectedPayoutStatus,
  setSelectedCooledOff,
  setSelectedReferrerUserId,
  setPage,
  setRowsPerPage,
  setPagination,
  setClaims,
  showFilterModal,
  showPayoutModal,
  showVoidModal,
  setSuccessMessage,
  setErrorMessage,
} = referralClaimSlice.actions;

export const selectState = (state) => state.referralClaim;

export const handleSelectClaimTab = (tab, campaignId) => (dispatch) => {
  dispatch(setSelectedPayoutStatus(tab.payoutStatus));
  dispatch(setSelectedCooledOff(tab.cooledOff ?? null));
  dispatch(setPage(0));
  dispatch(setSelectedIds([]));
  dispatch(setSelectedClaims([]));
  dispatch(handleGetReferralClaims(campaignId));
};

export const handleGetReferralClaims =
  (campaignId) => async (dispatch, getState) => {
    dispatch(showLoading());
    const state = getState();

    try {
      const response = await getReferralClaims(
        state.referralClaim.page,
        state.referralClaim.rowsPerPage,
        campaignId,
        state.referralClaim.selectedPayoutStatus,
        state.referralClaim.selectedCooledOff,
        state.referralClaim.selectedReferrerUserId,
        state.referralClaim.sort,
        state.referralClaim.sortBy
      );
      const { content, ...pagination } = response.data;

      dispatch(showFilterModal(false));
      dispatch(setPagination(pagination));
      dispatch(setClaims(content));
    } catch (error) {
      dispatch(setErrorMessage(error.message));
    } finally {
      dispatch(hideLoading());
    }
  };

/**
 * Confirming credits each referrer's wallet. The server reports one result per claim, so a
 * claim that could not be paid must be surfaced without hiding the ones that were.
 */
export const handleMarkPaid = (campaignId) => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    const ineligible = state.referralClaim.selectedClaims.some(
      (claim) =>
        claim.payoutStatus !== "payable" || moment(claim.payableAt).isAfter()
    );
    if (ineligible) {
      dispatch(
        setErrorMessage("Only Ready to pay claims can be credited.")
      );
      dispatch(showPayoutModal(false));
      return;
    }

    const response = await updatePayoutStatus(state.referralClaim.selectedIds);
    const results = response.data ?? [];
    const failed = results.filter((result) => !result.paid);
    const paidCount = results.length - failed.length;

    dispatch(showPayoutModal(false));
    if (failed.length) {
      dispatch(
        setErrorMessage(
          `Credited ${paidCount} of ${results.length} claim(s). Failed: ${failed
            .map((result) => `${result.claimId} (${result.reason})`)
            .join("; ")}`
        )
      );
    } else {
      dispatch(
        setSuccessMessage(`Credited ${paidCount} referrer wallet(s)!`)
      );
    }
    dispatch(setSelectedIds([]));
    dispatch(setSelectedClaims([]));
    dispatch(handleGetReferralClaims(campaignId));
  } catch (error) {
    dispatch(setErrorMessage(error.message));
  } finally {
    dispatch(hideLoading());
  }
};

/**
 * Cancelling is how a refunded purchase gets clawed back. The API voids one claim at a time,
 * so the failures are collected here to keep the same per-claim reporting as crediting.
 */
export const handleVoidClaims = (campaignId) => async (dispatch, getState) => {
  dispatch(showLoading());
  const state = getState();

  try {
    const ineligible = state.referralClaim.selectedClaims.some(
      (claim) => !VOIDABLE_STATUSES.includes(claim.payoutStatus)
    );
    if (ineligible) {
      dispatch(
        setErrorMessage("Only claims that have not been paid can be cancelled.")
      );
      dispatch(showVoidModal(false));
      return;
    }

    const claimIds = state.referralClaim.selectedIds;
    const outcomes = await Promise.allSettled(
      claimIds.map((claimId) => voidReferralClaim(claimId))
    );
    const failed = outcomes
      .map((outcome, index) => ({ outcome, claimId: claimIds[index] }))
      .filter(({ outcome }) => outcome.status === "rejected");

    dispatch(showVoidModal(false));
    if (failed.length) {
      dispatch(
        setErrorMessage(
          `Cancelled ${claimIds.length - failed.length} of ${
            claimIds.length
          } claim(s). Failed: ${failed
            .map(
              ({ claimId, outcome }) =>
                `${claimId} (${referralClaimErrorMessage(outcome.reason)})`
            )
            .join("; ")}`
        )
      );
    } else {
      dispatch(setSuccessMessage(`Cancelled ${claimIds.length} claim(s).`));
    }
    dispatch(setSelectedIds([]));
    dispatch(setSelectedClaims([]));
    dispatch(handleGetReferralClaims(campaignId));
  } catch (error) {
    dispatch(setErrorMessage(referralClaimErrorMessage(error)));
  } finally {
    dispatch(hideLoading());
  }
};

export const downloadReferralClaims =
  (campaignId) => async (dispatch, getState) => {
    dispatch(showLoading());
    const state = getState();

    try {
      const headers = {
        referrerName: "Referrer Name",
        referrerEmail: "Referrer Email",
        referrerUserId: "Referrer User ID",
        customerName: "Customer Name",
        customerEmail: "Customer Email",
        customerId: "Customer ID",
        sourceType: "Source Type",
        sourceId: "Source ID",
        rewardType: "Reward Type",
        creditToWalletAmount: "Credit Amount (RM)",
        yearMonth: "Year Month",
        payoutStatus: "Payout Status",
        payableAt: "Ready On",
        claimedAt: "Claimed At",
        paidAt: "Paid At",
      };

      const csvContent = [
        Object.values(headers).join(","),
        ...state.referralClaim.claims.map((obj) =>
          Object.keys(headers)
            .map((key) => {
              const value = obj[key];
              return referralClaimCsvCell(key === "creditToWalletAmount" ? centToDollar(value) : value);
            })
            .join(",")
        ),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
      const scope = campaignId ?? "all-campaigns";
      saveAs(blob, `referral-claims-${scope}-${moment().format("YYYY-MM-DD")}.csv`);
    } catch (error) {
      dispatch(setErrorMessage(error.message));
    } finally {
      dispatch(hideLoading());
    }
  };

// Voiding rejects an already paid claim with a message the admin needs to see,
// rather than a generic axios status text.
const referralClaimErrorMessage = (error) =>
  error?.response?.data?.message || error?.message || "Unexpected error";

export default referralClaimSlice.reducer;
